import { useState, useEffect, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Plus, X, Gear, Bank, Buildings, Factory, Question } from '@phosphor-icons/react'
import { Settings } from './Settings'
import { CreateEntityForm } from './CreateEntityForm'
import { ProductManager } from './ProductManager'
import { useEntityDetails } from '../hooks/useEntityDetails'
import { useContractRead, useContractReads } from 'wagmi'
import { CONFIG } from '../config'
import { Abi } from 'viem'
import { EntityCard } from './EntityCard'

// Map entity types to icons
const ENTITY_TYPE_ICONS: Record<string, typeof Bank> = {
  'Bank': Bank,
  'Corporation': Buildings,
  'Factory': Factory,
  'Unknown': Question,
}

export function TrinityMaster() {
  const { entities, isLoading } = useEntityDetails()
  
  const [selectedEntity, setSelectedEntity] = useState<`0x${string}` | undefined>()
  const [selectedProduct, setSelectedProduct] = useState<`0x${string}` | undefined>()
  const [isCreatingEntity, setIsCreatingEntity] = useState(false)

  // Get selected entity details
  const selectedEntityDetails = useMemo(() => 
    entities.find(e => e.address === selectedEntity), 
    [entities, selectedEntity]
  );

  // Get entity types for all entities
  const entityTypes = useContractReads({
    contracts: entities.map(entity => ({
      address: entity.address,
      abi: CONFIG.TRINITY_ENTITY_ABI.abi as unknown as Abi,
      functionName: 'entityType',
    })),
  });

  // Validate selected product exists in current entity
  useEffect(() => {
    if (selectedProduct && selectedEntityDetails) {
      const productExists = selectedEntityDetails.products.some(p => p.address === selectedProduct);
      if (!productExists) {
        console.log('Selected product not found in current entity, resetting selection');
        setSelectedProduct(undefined);
      }
    }
  }, [selectedProduct, selectedEntityDetails]);

  const handleProductSelect = (productAddress: `0x${string}` | undefined) => {
      setSelectedProduct(productAddress);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br p-8">
      <h1 className="text-4xl font-bold mb-3 bg-gradient-to-r from-blue-500/90 via-purple-500/90 to-pink-500/90 text-transparent bg-clip-text">Settings</h1>
      <p className="text-base text-secondary mb-8"></p>

      {/* Entity Selection */}
      {!isCreatingEntity && (
        <div className="glass-panel p-6 rounded-2xl mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold">Entities ({entities.length})</h3>
            <button 
              className="p-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-full transition-colors"
              onClick={() => setIsCreatingEntity(true)}
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-2">
            {entities.length > 0 ? (
              entities.map((entity, index) => {
                const entityType = entityTypes.data?.[index]?.result as string || 'Unknown';
                const EntityIcon = ENTITY_TYPE_ICONS[entityType] || ENTITY_TYPE_ICONS.Unknown;
                return (
                  <div
                    key={entity.address}
                    className="min-w-[300px]"
                  >
                    <EntityCard 
                      entity={entity} 
                      clickable={true}
                      navigateOnClick={false}
                      onClick={() => {
                        setSelectedEntity(entity.address)
                        setSelectedProduct(undefined)
                      }}
                    />
                  </div>
                )
              })
            ) : (
              <div className="text-center text-white/60 py-4 flex-1">No entities found</div>
            )}
          </div>
        </div>
      )}

      {/* Create Entity Form */}
        {isCreatingEntity && (
            <CreateEntityForm 
              onCancel={() => setIsCreatingEntity(false)}
              onSuccess={() => setIsCreatingEntity(false)}
            />
        )}

      {/* Product Manager */}
      {selectedEntity && !isCreatingEntity && (
          <ProductManager
          entity={selectedEntityDetails!}
          selectedProduct={selectedProduct}
            onProductSelect={handleProductSelect}
        />
      )}

      {/* Settings Panel */}
      {(selectedEntity || selectedProduct) && !isCreatingEntity && (
        <div className="mt-6">
              <Settings
                entityAddress={selectedEntity}
                productAddress={selectedProduct}
            type={selectedProduct ? 'product' : 'entity'}
          />
        </div>
      )}
    </div>
  );
} 